import { useEffect, useState, useRef } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../FirebaseConfig";
import { Link } from "react-router-dom";
import * as THREE from 'three';
import { motion } from 'framer-motion';
import { Phone, MessageSquare } from 'lucide-react';

const setupThreeJS = (canvasRef) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const sizes = {
    width: window.innerWidth,
    height: document.body.scrollHeight
  };

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 1000);
  camera.position.z = 50;

  const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    alpha: true,
    antialias: true
  });
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const particleCount = 1500;
  const positions = new Float32Array(particleCount * 3);
  const velocities = new Float32Array(particleCount * 3);

  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 200;
    positions[i * 3 + 1] = (Math.random() - 0.5) * 200;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 200;

    velocities[i * 3] = (Math.random() - 0.5) * 0.02;
    velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.02;
    velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.02;
  }

  const particleGeometry = new THREE.BufferGeometry();
  particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  
  const particleMaterial = new THREE.PointsMaterial({
    color: 0x4ADE80,
    size: 0.3,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.8
  });
  
  const particles = new THREE.Points(particleGeometry, particleMaterial);
  scene.add(particles);

  const clock = new THREE.Clock();
  const animate = () => {
    const elapsedTime = clock.getElapsedTime();

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] += velocities[i * 3] * Math.sin(elapsedTime * 0.1);
      positions[i * 3 + 1] += velocities[i * 3 + 1] * Math.cos(elapsedTime * 0.1);
      positions[i * 3 + 2] += velocities[i * 3 + 2] * Math.sin(elapsedTime * 0.1);

      if (Math.abs(positions[i * 3]) > 100) velocities[i * 3] *= -1;
      if (Math.abs(positions[i * 3 + 1]) > 100) velocities[i * 3 + 1] *= -1;
      if (Math.abs(positions[i * 3 + 2]) > 100) velocities[i * 3 + 2] *= -1;
    }
    
    particleGeometry.attributes.position.needsUpdate = true;

    particles.rotation.y = elapsedTime * 0.005;
    
    renderer.render(scene, camera);
    requestAnimationFrame(animate);
  };

  const onWindowResize = () => {
    sizes.width = window.innerWidth;
    sizes.height = document.body.scrollHeight;
    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();
    renderer.setSize(sizes.width, sizes.height);
  };
  window.addEventListener('resize', onWindowResize);

  animate();

  return () => {
    window.removeEventListener('resize', onWindowResize);
    renderer.dispose();
  };
};

export default function RestaurantMenu() {
  const [foodItems, setFoodItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleNumbers, setVisibleNumbers] = useState({});
  const canvasRef = useRef(null);

  useEffect(() => {
    const cleanup = setupThreeJS(canvasRef);
    return cleanup;
  }, []);

  useEffect(() => {
    const fetchApprovedFood = async () => {
      try {
        const q = query(collection(db, "foodItems"), where("approved", "==", true));
        const querySnapshot = await getDocs(q);
        
        const approvedItems = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setFoodItems(approvedItems);
      } catch (error) {
        console.error("Error fetching approved food items:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchApprovedFood();
  }, []);

  const toggleNumberVisibility = (id) => {
    setVisibleNumbers(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleMessage = (number, itemName) => {
    const message = encodeURIComponent(`I'm interested in your ${itemName} from uCart`);
    window.open(`https://wa.me/${number}?text=${message}`, '_blank');
  };

  return (
    <div className="bg-transparent min-h-screen text-white font-['Space_Grotesk'] relative overflow-hidden">
      <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full z-0 pointer-events-none" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row justify-between items-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-300 mb-4 md:mb-0">
            Culinary Delights
          </h2>
          <Link to="/foodform">
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-emerald-600 to-lime-600 hover:from-emerald-700 hover:to-lime-700 px-8 py-3 rounded-full font-semibold shadow-lg shadow-emerald-500/20 transition-all"
            >
              Become a Vendor
            </motion.button>
          </Link>
        </motion.div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 border-t-emerald-400 border-emerald-200 rounded-full"
            />
          </div>
        ) : foodItems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-gray-400 text-xl py-20 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10"
          >
            No culinary delights available yet. Check back soon!
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {foodItems.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white/5 backdrop-blur-xl rounded-2xl overflow-hidden shadow-xl border border-white/10 hover:border-emerald-400/30 hover:shadow-emerald-400/20 transition-all duration-300 flex flex-col"
              >
                <div className="relative h-56 md:h-64 overflow-hidden">
                  <img 
                    src={item.imageUrl} 
                    alt={item.name} 
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <div className="absolute top-4 right-4 bg-emerald-500/90 text-white px-3 py-1 rounded-full text-sm font-medium shadow-md">
                    ₦{item.price}
                  </div>
                    <div className="absolute top-4 right-4 bg-emerald-500/90 text-white px-3 py-1 rounded-full text-sm font-medium shadow-md">
                    ₦{item.university}
                  </div>
                </div>
                
                <div className="p-6 flex flex-col flex-grow">
                  <h4 className="text-xl md:text-2xl font-bold text-emerald-300 mb-2">{item.name}</h4>
                  <p className="text-gray-300 text-sm mb-4 flex-grow">{item.description}</p>
                  
                  <div className="space-y-3 mt-auto">
                    <p className="text-gray-400 text-sm flex items-center gap-2">
                      <span className="text-emerald-400">⏱</span> Prep time: {item.time}
                    </p>
                    
                    <div className="flex flex-col gap-2">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => toggleNumberVisibility(item.id)}
                        className="w-full bg-white/5 hover:bg-white/10 text-emerald-400 font-medium py-3 rounded-xl flex items-center justify-center gap-2 border border-white/20 transition-all"
                      >
                        <Phone size={18} />
                        {visibleNumbers[item.id] ? item.number : 'Show Contact Number'}
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleMessage(item.number, item.name)}
                        className="w-full bg-gradient-to-r from-emerald-600 to-lime-600 hover:from-emerald-700 hover:to-lime-700 text-white font-medium py-3 rounded-xl flex items-center justify-center gap-2 transition-all shadow-md"
                      >
                        <MessageSquare size={18} />
                        Message Vendor
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}